import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiClient } from '../api/client';
import { BackgroundJob, JobStatus } from '../types';

export interface JobProgress {
  current: number;
  total: number;
  message: string | null;
}

export interface UseBackgroundJobResult {
  status: JobStatus | null;
  progress: JobProgress | null;
  result: Record<string, unknown> | null;
  error: string | null;
  isLoading: boolean;
  isComplete: boolean;
  isFailed: boolean;
}

const INITIAL_POLL_INTERVAL = 2000;
const MAX_POLL_INTERVAL = 10000;
const BACKOFF_MULTIPLIER = 1.5;

/**
 * Hook to poll and track a background job's status
 */
export const useBackgroundJob = (jobId: string | null | undefined): UseBackgroundJobResult => {
  const [job, setJob] = useState<BackgroundJob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const pollIntervalRef = useRef(INITIAL_POLL_INTERVAL);
  const timeoutRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchJob = useCallback(async (id: string) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const response = await apiClient.get<BackgroundJob>(`/jobs/${id}`, {
        signal: abortControllerRef.current.signal,
      });
      setJob(response.data);
      setFetchError(null);
      return response.data;
    } catch (err: unknown) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'CanceledError') {
        return null;
      }
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to fetch job status';
      setFetchError(errorMessage);
      return null;
    }
  }, []);

  const scheduleNextPoll = useCallback((id: string) => {
    timeoutRef.current = window.setTimeout(async () => {
      const fetchedJob = await fetchJob(id);

      if (fetchedJob) {
        // Check if job is still in progress
        const isInProgress = fetchedJob.status === 'pending' || fetchedJob.status === 'running';

        if (isInProgress) {
          // Apply exponential backoff
          pollIntervalRef.current = Math.min(
            pollIntervalRef.current * BACKOFF_MULTIPLIER,
            MAX_POLL_INTERVAL
          );
          scheduleNextPoll(id);
        }
      }
    }, pollIntervalRef.current);
  }, [fetchJob]);

  useEffect(() => {
    // Reset state when jobId changes
    if (!jobId) {
      setJob(null);
      setIsLoading(false);
      setFetchError(null);
      pollIntervalRef.current = INITIAL_POLL_INTERVAL;
      return;
    }

    // Start polling
    setIsLoading(true);
    pollIntervalRef.current = INITIAL_POLL_INTERVAL;

    const startPolling = async () => {
      const fetchedJob = await fetchJob(jobId);
      setIsLoading(false);

      if (fetchedJob) {
        const isInProgress = fetchedJob.status === 'pending' || fetchedJob.status === 'running';
        if (isInProgress) {
          scheduleNextPoll(jobId);
        }
      }
    };

    startPolling();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [jobId, fetchJob, scheduleNextPoll]);

  const status = job?.status ?? null;
  const isComplete = status === 'completed';
  const isFailed = status === 'failed';

  const progress: JobProgress | null = useMemo(
    () =>
      job
        ? {
            current: job.progress_current,
            total: job.progress_total,
            message: job.progress_message,
          }
        : null,
    [job?.progress_current, job?.progress_total, job?.progress_message]
  );

  return {
    status,
    progress,
    result: job?.result ?? null,
    error: job?.error ?? fetchError,
    isLoading,
    isComplete,
    isFailed,
  };
};
